/**
 * Mobile-internal player data hook for Free Agents page.
 * Per Tony's mobile architecture rule: mobile-internal copy of desktop usePlayerData.
 *
 * Pagination:
 *   - Initial load (pageNum=0, !append): ALWAYS uses BATCH_SIZE=500 loop until has_more=false
 *     (matches desktop comment "Loading ALL players in batches (virtual scrolling)").
 *     showAll passes through as available_only on backend; both modes use the same batch loop.
 *   - loadMore: PLAYERS_PER_PAGE=100 single-page fetch with offset = pageNum * 100.
 *
 * applyFrontendFilters reads canonical post-9m advancedFilters keys:
 *   minG/maxG (NOT minGames/maxGames)
 *   hitterQualified/pitcherQualified (NOT mlbQualifiedHitters/mlbQualifiedPitchers)
 * Per Cluster A 9m bug-class closure. Diverging from these keys silently breaks 11 filters.
 *
 * DD-FASEARCH-RACE-0730: ported the desktop usePlayerData fetch-race fix —
 *   (1) latest-request guard (monotonic id + isCurrent before every state write +
 *       effect-cleanup abort/bump); (2) params pinned at kickoff (frozen descriptor);
 *   (3) commissioner-at-source (isCommissionerMode -> resolveInclude40, ref removed);
 *   (4) 300ms search debounce. Stale/out-of-order responses can no longer overwrite
 *   the active query, and no fetch runs with a lagging commissioner flag.
 *
 * Shared dependencies (allowed): services/apiService (leaguesAPI only).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { leaguesAPI } from '../../../../../services/apiService';
import { formatApiError } from '../../../../../services/errorUtils';

const PLAYERS_PER_PAGE = 100;
const BATCH_SIZE = 500;

const OFFSEASON_STATUSES = ['setup', 'draft', 'finalizing'];

export const useMobilePlayerData = (leagueId, filters = {}, leagueContext = {}, isCommissionerMode = null) => {
  const {
    activeTab = 'hitting',
    positionFilter = 'all',
    searchTerm = '',
    showAll = false,
    advancedFilters = {},
    statsYear = 'current',
    includeNon40Man = false,
  } = filters;

  const isHistorical = statsYear === 'previous';

  const {
    transactionsEnabled = false,
    savedPrices = {},
    pricesExist = false,
    leagueStatus,
  } = leagueContext;

  // DD-FASEARCH-RACE-0730 — latest-request guard (correctness core). Every load
  // captures a monotonic id; a response whose id is no longer the latest may NEVER
  // write player state. The AbortController cancels the now-wasted network.
  const latestRequestIdRef = useRef(0);
  const abortRef = useRef(null);

  const beginRequest = useCallback(() => {
    const requestId = ++latestRequestIdRef.current;
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) { /* no-op */ }
    }
    const controller = new AbortController();
    abortRef.current = controller;
    return { requestId, signal: controller.signal };
  }, []);

  const isCurrent = useCallback(
    (requestId) => requestId === latestRequestIdRef.current,
    [],
  );

  // Debounce the search term so rapid typing collapses to ONE load.
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // include_non_40man derived from the AUTHORITATIVE commissioner value at query
  // construction — NOT the render-late setIncludeNon40Man effect. Falls back to the
  // filters flag when the caller doesn't pass isCommissionerMode.
  const resolveInclude40 = useCallback(
    () => (isCommissionerMode != null ? !!isCommissionerMode : includeNon40Man),
    [isCommissionerMode, includeNon40Man],
  );

  // Core data state
  const [players, setPlayers] = useState([]);
  const [unfilteredPlayers, setUnfilteredPlayers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Loading state
  const [loading, setLoading] = useState(false);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [allDataLoaded, setAllDataLoaded] = useState(false);

  const applyFrontendFilters = useCallback((playersToFilter) => {
    if (!advancedFilters) return playersToFilter;

    let filtered = [...playersToFilter];

    if (activeTab === 'hitting') {
      // MLB Qualified filter for hitters: PA = AB + BB + HBP + SF + SH >= qualifiedPA
      if (advancedFilters.hitterQualified) {
        const minPA = advancedFilters.qualifiedPA || 502;
        filtered = filtered.filter((p) => {
          const h = p.stats?.season?.hitting;
          const pa =
            (h?.at_bats || 0) +
            (h?.walks || 0) +
            (h?.hit_by_pitch || 0) +
            (h?.sacrifice_flies || 0) +
            (h?.sacrifice_hits || 0);
          return pa >= minPA;
        });
      }
      if (advancedFilters.minAB) {
        filtered = filtered.filter((p) => (p.stats?.season?.hitting?.at_bats || 0) >= parseInt(advancedFilters.minAB, 10));
      }
      if (advancedFilters.maxAB) {
        filtered = filtered.filter((p) => (p.stats?.season?.hitting?.at_bats || 0) <= parseInt(advancedFilters.maxAB, 10));
      }
      if (advancedFilters.minHR) {
        filtered = filtered.filter((p) => (p.stats?.season?.hitting?.home_runs || 0) >= parseInt(advancedFilters.minHR, 10));
      }
      if (advancedFilters.maxHR) {
        filtered = filtered.filter((p) => (p.stats?.season?.hitting?.home_runs || 0) <= parseInt(advancedFilters.maxHR, 10));
      }
      if (advancedFilters.minG) {
        filtered = filtered.filter((p) => (p.stats?.season?.hitting?.games_played || 0) >= parseInt(advancedFilters.minG, 10));
      }
      if (advancedFilters.maxG) {
        filtered = filtered.filter((p) => (p.stats?.season?.hitting?.games_played || 0) <= parseInt(advancedFilters.maxG, 10));
      }
    } else if (activeTab === 'pitching') {
      if (advancedFilters.pitcherQualified) {
        const minIP = advancedFilters.qualifiedIP || 162;
        filtered = filtered.filter((p) => (p.stats?.season?.pitching?.innings_pitched || 0) >= minIP);
      }
      if (advancedFilters.minK) {
        filtered = filtered.filter((p) => (p.stats?.season?.pitching?.strikeouts_pitched || 0) >= parseInt(advancedFilters.minK, 10));
      }
      if (advancedFilters.maxK) {
        filtered = filtered.filter((p) => (p.stats?.season?.pitching?.strikeouts_pitched || 0) <= parseInt(advancedFilters.maxK, 10));
      }
      if (advancedFilters.minGS) {
        filtered = filtered.filter((p) => (p.stats?.season?.pitching?.games_started || 0) >= parseInt(advancedFilters.minGS, 10));
      }
      if (advancedFilters.maxGS) {
        filtered = filtered.filter((p) => (p.stats?.season?.pitching?.games_started || 0) <= parseInt(advancedFilters.maxGS, 10));
      }
      if (advancedFilters.minIP) {
        filtered = filtered.filter((p) => (p.stats?.season?.pitching?.innings_pitched || 0) >= parseFloat(advancedFilters.minIP));
      }
      if (advancedFilters.maxIP) {
        filtered = filtered.filter((p) => (p.stats?.season?.pitching?.innings_pitched || 0) <= parseFloat(advancedFilters.maxIP));
      }
    }

    // Price filter (both tabs) — offseason-aware contract resolution
    const filterIsOffseason = OFFSEASON_STATUSES.includes(leagueStatus);
    if (advancedFilters.minPrice) {
      filtered = filtered.filter((p) => {
        const contract = filterIsOffseason && p.projected_contract ? p.projected_contract : p.league_context?.financial;
        const price = savedPrices[p.ids?.mlb] ?? contract?.baseline_price;
        return price >= parseInt(advancedFilters.minPrice, 10);
      });
    }
    if (advancedFilters.maxPrice) {
      filtered = filtered.filter((p) => {
        const contract = filterIsOffseason && p.projected_contract ? p.projected_contract : p.league_context?.financial;
        const price = savedPrices[p.ids?.mlb] ?? contract?.baseline_price;
        return price <= parseInt(advancedFilters.maxPrice, 10);
      });
    }

    return filtered;
  }, [advancedFilters, activeTab, savedPrices, leagueStatus]);

  // Apply position-filter expansion + pricing decoration to a batch of API players
  const decorateBatch = useCallback(
    (batchPlayers) => {
      const batchIsOffseason = OFFSEASON_STATUSES.includes(leagueStatus);
      let decorated = batchPlayers.map((player) => {
        const playerId = player.ids?.mlb;
        const contract =
          batchIsOffseason && player.projected_contract ? player.projected_contract : player.league_context?.financial;
        const price = savedPrices[playerId] ?? contract?.baseline_price;
        return {
          ...player,
          price,
          display_price: price,
          display_salary: contract?.contract_salary,
        };
      });

      if (positionFilter === 'MI') {
        decorated = decorated.filter((p) => p.info?.position === '2B' || p.info?.position === 'SS');
      } else if (positionFilter === 'CI') {
        decorated = decorated.filter((p) => p.info?.position === '1B' || p.info?.position === '3B');
      } else if (positionFilter === 'OF') {
        decorated = decorated.filter((p) => ['OF', 'LF', 'CF', 'RF'].includes(p.info?.position));
      } else if (positionFilter !== 'all') {
        decorated = decorated.filter((p) => p.info?.position === positionFilter);
      }
      return decorated;
    },
    [leagueStatus, savedPrices, positionFilter],
  );

  // Load ALL players in BATCH_SIZE batches — virtual-scrolling load path.
  // Live progress: setPlayers/setTotalCount after each batch.
  const loadAllPlayers = useCallback(async () => {
    // 9o.6.e Slice 2 Commit 7-rest: pending-custom-input guard. When dateRange='custom'
    // but either date is empty, user is mid-input — skip fetch to avoid backend 422.
    const dateRange = advancedFilters?.dateRange ?? 'season';
    if (dateRange === 'custom') {
      if (!advancedFilters?.customStartDate || !advancedFilters?.customEndDate) {
        return;
      }
    }

    // DD-FASEARCH-RACE-0730: pin an immutable query descriptor at kickoff; the whole
    // paginated loop uses it — never a ref that can mutate mid-run.
    const { requestId, signal } = beginRequest();
    const query = Object.freeze({
      search: (debouncedSearch || '').trim(),
      include_non_40man: resolveInclude40(),
      activeTab,
      positionFilter,
      showAll,
      isHistorical,
      dateRange,
      customStartDate: dateRange === 'custom' ? advancedFilters.customStartDate : null,
      customEndDate: dateRange === 'custom' ? advancedFilters.customEndDate : null,
    });

    // Synchronous clears at the top, BEFORE the first await, so the next
    // render shows spinner + empty state — not stale rows from the previous
    // tab/filter/window selection.
    setLoading(true);
    setPlayers([]);
    setUnfilteredPlayers([]);
    setTotalCount(0);
    setPage(0);
    setHasMore(true);
    setAllDataLoaded(false);
    setError('');
    setSuccessMessage('');

    let allPlayers = [];
    let currentOffset = 0;
    let hasMoreData = true;

    try {
      while (hasMoreData) {
        const apiFilters = {
          position: query.positionFilter !== 'all' ? query.positionFilter : query.activeTab,
          limit: BATCH_SIZE,
          offset: currentOffset,
          show_all: query.showAll,
          is_historical: query.isHistorical,
          include_non_40man: query.include_non_40man,
          dateRange: query.dateRange,
          customStartDate: query.customStartDate,
          customEndDate: query.customEndDate,
        };
        if (query.search) {
          apiFilters.search = query.search;
        }
        const sortParams = {
          sort_by: query.activeTab === 'hitting' ? 'at_bats' : 'games_started',
          sort_order: 'desc',
        };

        const response = await leaguesAPI.getFreeAgentsCanonical(
          leagueId,
          { ...apiFilters, ...sortParams },
          { signal },
        );

        // GUARD: a newer load superseded this one — discard, write nothing.
        if (!isCurrent(requestId)) return;

        if (response.success) {
          const batchPlayers = response.players || [];
          const decorated = decorateBatch(batchPlayers);
          allPlayers = [...allPlayers, ...decorated];
          hasMoreData = response.has_more || false;
          currentOffset += BATCH_SIZE;

          if (!isCurrent(requestId)) return;
          setUnfilteredPlayers(allPlayers);
          const filteredPlayers = applyFrontendFilters(allPlayers);
          setPlayers(filteredPlayers);
          setTotalCount(filteredPlayers.length);
        } else {
          console.error('Failed to load batch:', response.message);
          hasMoreData = false;
        }
      }
      if (!isCurrent(requestId)) return;
      setAllDataLoaded(true);
      setHasMore(false);
    } catch (err) {
      if (signal.aborted || !isCurrent(requestId)) return;
      console.error('Error loading all players:', err);
      setError(formatApiError(err, 'Failed to load all players'));
    } finally {
      if (isCurrent(requestId)) setLoading(false);
    }
  }, [leagueId, debouncedSearch, activeTab, positionFilter, showAll, isHistorical, decorateBatch, applyFrontendFilters, resolveInclude40, beginRequest, isCurrent, advancedFilters?.dateRange, advancedFilters?.customStartDate, advancedFilters?.customEndDate]);

  const loadPlayers = useCallback(
    async (pageNum = 0, append = false) => {
      // Initial load always batches through loadAllPlayers (virtual scrolling).
      if (pageNum === 0 && !append) {
        return loadAllPlayers();
      }

      // 9o.6.e Slice 2: pending-custom-input guard (see loadAllPlayers above).
      const dateRange = advancedFilters?.dateRange ?? 'season';
      if (dateRange === 'custom') {
        if (!advancedFilters?.customStartDate || !advancedFilters?.customEndDate) {
          return;
        }
      }

      // Append/pagination path is a CONTINUATION of the active query — captures the
      // current latest id (does not start a new one) and bails if a newer load began.
      const requestId = latestRequestIdRef.current;
      const signal = abortRef.current?.signal;
      const query = Object.freeze({
        search: (debouncedSearch || '').trim(),
        include_non_40man: resolveInclude40(),
        activeTab,
        positionFilter,
        showAll,
        isHistorical,
        dateRange,
        customStartDate: dateRange === 'custom' ? advancedFilters.customStartDate : null,
        customEndDate: dateRange === 'custom' ? advancedFilters.customEndDate : null,
      });

      if (pageNum === 0) {
        setLoading(true);
        setPlayers([]);
        setUnfilteredPlayers([]);
        setTotalCount(0);
        setPage(0);
        setHasMore(true);
        setAllDataLoaded(false);
      } else {
        setBackgroundLoading(true);
      }

      setError('');
      setSuccessMessage('');

      try {
        const apiFilters = {
          position: query.positionFilter !== 'all' ? query.positionFilter : query.activeTab,
          limit: PLAYERS_PER_PAGE,
          offset: pageNum * PLAYERS_PER_PAGE,
          is_historical: query.isHistorical,
        };
        if (query.search) {
          apiFilters.search = query.search;
        }
        const sortParams = {
          sort_by: query.activeTab === 'hitting' ? 'at_bats' : 'games_started',
          sort_order: 'desc',
        };
        const response = await leaguesAPI.getFreeAgentsCanonical(
          leagueId,
          {
            ...apiFilters,
            ...sortParams,
            show_all: query.showAll,
            include_non_40man: query.include_non_40man,
            dateRange: query.dateRange,
            customStartDate: query.customStartDate,
            customEndDate: query.customEndDate,
          },
          { signal },
        );

        if (!isCurrent(requestId)) return;

        if (response.success) {
          const decorated = decorateBatch(response.players || []);
          if (append && pageNum > 0) {
            setUnfilteredPlayers((prev) => [...prev, ...decorated]);
          } else {
            setUnfilteredPlayers(decorated);
          }
          const filteredPlayers = applyFrontendFilters(decorated);
          if (append && pageNum > 0) {
            setPlayers((prev) => [...prev, ...filteredPlayers]);
          } else {
            setPlayers(filteredPlayers);
          }
          if (pageNum === 0) {
            setTotalCount(filteredPlayers.length);
          }
          setHasMore(response.has_more || false);
          setPage(pageNum);
        } else {
          setError(response.message || `Failed to load ${showAll ? 'all players' : 'free agents'}`);
          if (!append) setPlayers([]);
        }
      } catch (err) {
        if (signal?.aborted || !isCurrent(requestId)) return;
        console.error(`Error loading ${showAll ? 'all players' : 'free agents'}:`, err);
        setError(formatApiError(err, `Failed to load ${showAll ? 'all players' : 'free agents'}`));
        if (!append) setPlayers([]);
      } finally {
        if (isCurrent(requestId)) {
          if (pageNum === 0) setLoading(false);
          else setBackgroundLoading(false);
        }
      }
    },
    [leagueId, debouncedSearch, activeTab, positionFilter, showAll, isHistorical, decorateBatch, applyFrontendFilters, loadAllPlayers, resolveInclude40, isCurrent, advancedFilters?.dateRange, advancedFilters?.customStartDate, advancedFilters?.customEndDate],
  );

  const loadMore = useCallback(() => {
    if (!backgroundLoading && hasMore) {
      loadPlayers(page + 1, true);
    }
  }, [backgroundLoading, hasMore, page, loadPlayers]);

  // Initial load when context is ready. Cleanup invalidates any in-flight load
  // (bump id + abort) on any dep change so a superseded response can't paint.
  useEffect(() => {
    if (leagueContext.isReady && (transactionsEnabled || leagueContext.browseMode)) {
      loadPlayers(0, false);
    }
    return () => {
      latestRequestIdRef.current += 1;
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch (_) { /* no-op */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    leagueContext.isReady,
    transactionsEnabled,
    leagueContext.browseMode,
    activeTab,
    positionFilter,
    showAll,
    debouncedSearch,
    isHistorical,
    includeNon40Man,
    isCommissionerMode,
    advancedFilters?.dateRange,
    advancedFilters?.customStartDate,
    advancedFilters?.customEndDate,
  ]);

  // Re-apply filters when advancedFilters change
  useEffect(() => {
    if (unfilteredPlayers.length > 0) {
      const filtered = applyFrontendFilters(unfilteredPlayers);
      setPlayers(filtered);
      setTotalCount(filtered.length);
    }
  }, [advancedFilters, unfilteredPlayers, applyFrontendFilters]);

  return {
    // Data
    players,
    setPlayers,
    totalCount,
    setTotalCount,

    // Loading states
    loading,
    backgroundLoading,
    error,
    setError,
    successMessage,
    setSuccessMessage,

    // Pagination
    page,
    hasMore,
    allDataLoaded,

    // Functions
    loadPlayers,
    loadMore,
  };
};

export default useMobilePlayerData;
